import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { authAPI } from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b7bb9a58-a668-4de2-aa2e-f5e39e6e614e/artifacts/buijzrp9_App%20Icon%20Yellow.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.resetPasswordDirect(email, newPassword, confirmPassword);
      setSuccessMessage('Password reset successfully. Redirecting to login...');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setErrorMessage(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="forgot-password-page">
      <div className="login-card">
        <div className="flex justify-center mb-6">
          <img
            src={LOGO_URL}
            alt="Mayo Logo"
            className="h-16 w-16 rounded-xl"
            data-testid="forgot-password-logo"
          />
        </div>

        <h1
          className="text-2xl font-bold text-center text-[#111111] mb-2"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Reset Password
        </h1>
        <p className="text-center text-[#666666] text-sm mb-6">
          Enter your email and choose a new password
        </p>

        {errorMessage && (
          <div
            className="mb-4 p-3 bg-red-50 border border-[#D32F2F] rounded-md text-[#D32F2F] text-sm"
            data-testid="forgot-password-error"
          >
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div
            className="mb-4 p-3 bg-green-50 border border-green-600 rounded-md text-green-700 text-sm"
            data-testid="forgot-password-success"
          >
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#111111] font-medium text-sm">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              data-testid="forgot-password-email-input"
              className="border-[#E0E0E0] bg-[#F5F5F5] focus:ring-[#FF6B35] focus:border-[#FF6B35] text-[#111111] placeholder:text-[#666666]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-[#111111] font-medium text-sm">
              New Password
            </Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={4}
              data-testid="forgot-password-new-input"
              className="border-[#E0E0E0] bg-[#F5F5F5] focus:ring-[#FF6B35] focus:border-[#FF6B35] text-[#111111] placeholder:text-[#666666]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-[#111111] font-medium text-sm">
              Confirm New Password
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={4}
              data-testid="forgot-password-confirm-input"
              className="border-[#E0E0E0] bg-[#F5F5F5] focus:ring-[#FF6B35] focus:border-[#FF6B35] text-[#111111] placeholder:text-[#666666]"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            data-testid="forgot-password-submit-button"
            className="w-full bg-[#FF6B35] hover:bg-[#e55a2b] text-white font-medium py-2 rounded-md transition-colors mt-6"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Resetting...
              </span>
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>

        <div className="text-center mt-6">
          <Link
            to="/login"
            data-testid="forgot-password-back-to-login-link"
            className="text-sm text-[#FF6B35] hover:text-[#e55a2b] font-medium"
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
